import { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Check, Eye, LucideClipboardCheck, RemoveFormatting, X, Undo2, FileText, ThumbsUp, ThumbsDown, History, RotateCcw, Rewind, Download, ChevronLeft, ChevronRight, UserX, ListChecks, CheckCheck, BadgeCheck, Clock, ArrowLeftRight } from "lucide-react";
import { ADVANCE_REQUEST_URL, LEAVE_REQUEST_URL, REIMBURESMENT_REQUEST_URL, RESIGNATION_REQUEST_URL, OVERTIME_APPROVAL_URL, SHIFT_CHANGE_REQUEST } from "../../constants/api";
import { truncateText } from "../../components/common/CommonFunctions";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { DatePicker, Image, Select } from "antd";
import dayjs from 'dayjs';
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";
import { isOvertimeRequestWorkflowEnabled } from "../../utils/customConfigSession";
import { usePayrollProcessLocked } from "../../hooks/usePayrollProcessLocked";
import PayrollProcessLockNotice from "../../components/payroll/PayrollProcessLockNotice";

const ApprovalInbox = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { update } = useApiMethods()
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [reimbursements, setReimbursements] = useState([]);
    const [advanceSalary, setAdvanceSalary] = useState([]);
    const [overtimeRequests, setOvertimeRequests] = useState([]);
    const [resignationrequest, setResignationrequest] = useState([]);
    const [shiftChanges, setShiftChanges] = useState([]);

    const { showBranchColumn, insertBranchColumn, TempTransferBadge } = useOrgAdminBranchColumn("employee");
    const showOvertimeTab = isOvertimeRequestWorkflowEnabled();
    const payrollLocked = usePayrollProcessLocked();

    const resolvedInitialTab = ['leave', 'reimbursement', 'advance', ...(showOvertimeTab ? ['overtime'] : []), 'resign', 'shiftchange'].includes(location.state?.activeTab)
        ? location.state.activeTab
        : 'leave';
    const [activeTab, setActiveTab] = useState(resolvedInitialTab);
    const [totalRecords, setTotalRecords] = useState(0)
    const [serverTrigger, setServerTrigger] = useState(false);

    // Dialog states
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showRevertDialog, setShowRevertDialog] = useState(false);
    const [showApprovalLog, setShowApprovalLog] = useState(false);
    const [showApproveDialog, setShowApproveDialog] = useState(false);
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const [revertReason, setRevertReason] = useState('');
    const [approvalNotes, setApprovalNotes] = useState('');
    const [debitFrom, setDebitFrom] = useState('');
    const [approvedEmiPeriod, setApprovedEmiPeriod] = useState('');
    const [selectedRequestForReject, setSelectedRequestForReject] = useState(null);
    const [reimbursementApprovedAmount, setReimbursementApprovedAmount] = useState('');
    const [overtimeApprovedHours, setOvertimeApprovedHours] = useState('');
    const [advanceApprovedAmount, setAdvanceApprovedAmount] = useState('');

    useEffect(() => {
        if (!showOvertimeTab && activeTab === 'overtime') {
            setActiveTab('leave');
        }
    }, [showOvertimeTab, activeTab]);

    // Resignation approval form (only used when the resignation dialog is open)
    const [resignationApproval, setResignationApproval] = useState({
        notice_period: '',
        last_working_day: null,
        approval_comments: '',
        is_payroll_hold: false,
    });

    // Prefill resignation approval fields from the selected row when the dialog opens
    useEffect(() => {
        if (!showApproveDialog || !selectedRequest || activeTab !== 'resign') return;
        const isFinalLevel = String(selectedRequest.level) === '2';
        const np = isFinalLevel ? selectedRequest.final_notice_period : selectedRequest.notice_period;
        const lwdRaw = isFinalLevel ? selectedRequest.final_last_working_day : selectedRequest.last_working_day;
        const comments = isFinalLevel ? selectedRequest.final_approval_comments : selectedRequest.approval_comments;
        const obligations = selectedRequest.salary_obligations || {};
        setResignationApproval({
            notice_period: (np !== null && np !== undefined && np !== '') ? String(np) : '',
            last_working_day: lwdRaw && lwdRaw !== 'NAT' && lwdRaw !== 'NaT' ? dayjs(lwdRaw) : null,
            approval_comments: comments || '',
            is_payroll_hold: !!(obligations.suggest_payroll_hold || obligations.is_payroll_hold),
        });
    }, [showApproveDialog, selectedRequest, activeTab]);

    // Default the approved-amount input to the requested amount whenever the reimbursement
    // approval dialog opens; approvers can edit it to trigger a 'partial' approval.
    useEffect(() => {
        if (!showApproveDialog || !selectedRequest || activeTab !== 'reimbursement') return;
        const requested = selectedRequest.requested_amount ?? selectedRequest.amount ?? '';
        setReimbursementApprovedAmount(requested === null || requested === undefined ? '' : String(requested));
    }, [showApproveDialog, selectedRequest, activeTab]);

    useEffect(() => {
        if (!showApproveDialog || !selectedRequest || activeTab !== 'overtime') return;
        const requested = selectedRequest.requested_over_time || selectedRequest.detected_over_time || '';
        setOvertimeApprovedHours(requested === null || requested === undefined ? '' : String(requested));
    }, [showApproveDialog, selectedRequest, activeTab]);

    useEffect(() => {
        if (!showApproveDialog || !selectedRequest || activeTab !== 'advance') return;
        const requestedEmi = selectedRequest.requested_emi_period ?? selectedRequest.emi_period ?? '';
        setApprovedEmiPeriod(requestedEmi === null || requestedEmi === undefined ? '' : String(requestedEmi));
        const requestedAmt = selectedRequest.advance_requested ?? selectedRequest.advance_approved ?? '';
        setAdvanceApprovedAmount(requestedAmt === null || requestedAmt === undefined ? '' : String(requestedAmt));
        if (selectedRequest.debit_from) {
            setDebitFrom(dayjs(selectedRequest.debit_from).format('YYYY-MM'));
        } else {
            setDebitFrom('');
        }
    }, [showApproveDialog, selectedRequest, activeTab]);

    // Loading states
    const [approveLoading, setApproveLoading] = useState(false);
    const [rejectLoading, setRejectLoading] = useState(false);
    const [revertLoading, setRevertLoading] = useState(false);
    const [showProcessedDialog, setShowProcessedDialog] = useState(false);
    const [processedRemarks, setProcessedRemarks] = useState('');
    const [processedLoading, setProcessedLoading] = useState(false);

    // Common handlers

    const handleRejectRequest = () => {
        // console.log('Rejecting request:', selectedRequest, 'Notes:', approvalNotes);
        setShowApproveDialog(false);
        setApprovalNotes('');
    };

    const handleApproveRequest = async () => {
        if (payrollLocked && selectedRequest?.approval_type === 'Overtime') {
            ToastMsg('error', 'Overtime approvals are blocked while payroll is locked');
            return;
        }
        setApproveLoading(true);
        let payload;

        console.log(selectedRequest);

        // Validation for advance salary
        if (selectedRequest.approval_type === 'Advance Salary') {
            if (!debitFrom || debitFrom.trim() === '') {
                ToastMsg('error', 'Debit from month is required for advance salary approval');
                setApproveLoading(false);
                return;
            }
            const debitMonth = dayjs(`${debitFrom}-01`);
            const currentMonth = dayjs().startOf('month');
            if (debitMonth.isBefore(currentMonth, 'month')) {
                ToastMsg('error', 'Debit from month cannot be in the past');
                setApproveLoading(false);
                return;
            }
            if (!approvedEmiPeriod || Number(approvedEmiPeriod) < 1) {
                ToastMsg('error', 'EMI period must be between 1 and 12 months');
                setApproveLoading(false);
                return;
            }
        }

        // Resignation request payload — gets priority because some resignations may also carry an employee shape
        if (tableData.type === 'resignation') {
            const level = parseInt(selectedRequest.level, 10) || 1;
            const isFinalLevel = level === 2;
            const noticePeriod = resignationApproval.notice_period;
            const lwd = resignationApproval.last_working_day;
            const comments = resignationApproval.approval_comments;

            if (noticePeriod === '' || noticePeriod === null || noticePeriod === undefined) {
                ToastMsg('error', 'Notice Period is required');
                setApproveLoading(false);
                return;
            }
            if (!lwd) {
                ToastMsg('error', 'Last Working Day is required');
                setApproveLoading(false);
                return;
            }
            if (!comments || !comments.trim()) {
                ToastMsg('error', 'Approval comments are required');
                setApproveLoading(false);
                return;
            }

            // Pull approver IDs out of the row's nested objects — the backend serializer
            // (ResignationApprovalLogSerializer) needs a PK, not the profile object.
            const approverId =
                selectedRequest.approved_by?.id ??
                selectedRequest.approved_by ??
                null;
            const finalApproverId =
                selectedRequest.final_approved_by?.id ??
                selectedRequest.final_approved_by ??
                null;

            payload = {
                id: selectedRequest.id,
                level,
                approval_status: 'approved',
            };

            if (isFinalLevel) {
                payload.final_approved_by = finalApproverId;
                payload.final_approval_date = dayjs().format('YYYY-MM-DD');
                payload.final_notice_period = parseInt(noticePeriod, 10);
                payload.final_last_working_day = dayjs(lwd).format('YYYY-MM-DD');
                payload.final_approval_comments = comments;
            } else {
                payload.approved_by = approverId;
                payload.approval_date = dayjs().format('YYYY-MM-DD');
                payload.notice_period = parseInt(noticePeriod, 10);
                payload.last_working_day = dayjs(lwd).format('YYYY-MM-DD');
                payload.approval_comments = comments;
            }
            payload.is_payroll_hold = !!resignationApproval.is_payroll_hold;
        } else if (selectedRequest.leave_request_type || selectedRequest.leave_type_id) {
            // Leave request payload
            payload = {
                ...selectedRequest,
                approval_notes: approvalNotes,
                approval_status: selectedRequest.request_type === 'cancel request' ? 'request cancelled' : 'approved',
                company: String(selectedRequest.company) || null,
                leave_type: selectedRequest.leave_type_id || null,
            }
            delete payload.leave_type_id;
            delete payload.employee;
        } else if (selectedRequest.reimbursements_type) {
            // Reimbursement request payload — approved_amount comes from the editable input.
            // The backend promotes the status to 'partial' when approved < requested.
            const requestedAmt = Number(selectedRequest.requested_amount || 0);
            const approvedAmtRaw = reimbursementApprovedAmount === '' ? requestedAmt : Number(reimbursementApprovedAmount);
            const approvedAmt = Number.isFinite(approvedAmtRaw) ? approvedAmtRaw : 0;
            if (selectedRequest.request_type !== 'cancel request') {
                if (approvedAmt <= 0) {
                    ToastMsg('error', 'Approved amount must be greater than zero');
                    setApproveLoading(false);
                    return;
                }
                if (approvedAmt > requestedAmt) {
                    ToastMsg('error', 'Approved amount cannot exceed requested amount');
                    setApproveLoading(false);
                    return;
                }
            }
            payload = {
                id: selectedRequest.id,
                approval_status: selectedRequest.request_type === 'cancel request' ? 'request cancelled' : "approved",
                approved_amount: approvedAmt,
                description: selectedRequest.description,
                reimbursement_type: selectedRequest.reimbursements_type.id,
                requested_amount: selectedRequest.requested_amount,
                request_type: "reimbursement request",
                approval_notes: approvalNotes,
                uploaded_documents: selectedRequest.uploaded_documents || [],
            }
        } else if (selectedRequest.approval_type === 'Overtime') {
            const requestedOt = selectedRequest.requested_over_time || selectedRequest.detected_over_time || '';
            const approvedOt = overtimeApprovedHours === '' ? requestedOt : overtimeApprovedHours;
            if (selectedRequest.request_type !== 'cancel request') {
                const approvedSeconds = parseOvertimeSeconds(approvedOt);
                const detectedSeconds = parseOvertimeSeconds(selectedRequest.detected_over_time || requestedOt);
                if (approvedSeconds === null) {
                    ToastMsg('error', 'Approved overtime must be in HH:MM:SS format');
                    setApproveLoading(false);
                    return;
                }
                if (approvedSeconds <= 0) {
                    ToastMsg('error', 'Approved overtime must be greater than zero');
                    setApproveLoading(false);
                    return;
                }
                if (detectedSeconds !== null && detectedSeconds > 0 && approvedSeconds > detectedSeconds) {
                    ToastMsg('error', 'Approved overtime cannot exceed detected overtime');
                    setApproveLoading(false);
                    return;
                }
            }
            payload = {
                id: selectedRequest.id,
                approval_status: selectedRequest.request_type === 'cancel request' ? 'request cancelled' : 'approved',
                approved_over_time: approvedOt,
                approval_notes: approvalNotes,
                request_type: 'overtime request',
            };
        } else {
            // Advance salary request payload
            const requestedAdvance = Number(selectedRequest.advance_requested || 0);
            const approvedAdvanceRaw = advanceApprovedAmount === '' ? requestedAdvance : Number(advanceApprovedAmount);
            if (selectedRequest.request_type !== 'cancel request') {
                if (!Number.isFinite(approvedAdvanceRaw) || approvedAdvanceRaw <= 0) {
                    ToastMsg('error', 'Please enter a valid approved amount');
                    setApproveLoading(false);
                    return;
                }
                if (approvedAdvanceRaw > requestedAdvance) {
                    ToastMsg('error', 'Approved amount cannot exceed the requested amount');
                    setApproveLoading(false);
                    return;
                }
            }
            payload = {
                ...selectedRequest,
                emi_period: Number(approvedEmiPeriod),
                approved_amount: selectedRequest.request_type === 'cancel request' ? selectedRequest.advance_approved : approvedAdvanceRaw,
                reason: selectedRequest.reason,
                advance_requested: selectedRequest.advance_requested,
                debit_from: debitFrom.includes('-') && debitFrom.length === 7 ? `${debitFrom}-01` : debitFrom,
                requested_emi_period: selectedRequest.requested_emi_period ?? selectedRequest.emi_period,
                uploaded_documents: selectedRequest.uploaded_documents || [],
                request_type: "advance request",
                approval_notes: approvalNotes,
                approval_status: selectedRequest.request_type === 'cancel request' ? 'cancelled' : "approved"
            }
        }

        const response = await update(tableData.url, payload);

        if (response.status === 'success') {
            ToastMsg('success', response.message);
            setShowApproveDialog(false);
            setApprovalNotes('');
            setResignationApproval({ notice_period: '', last_working_day: null, approval_comments: '', is_payroll_hold: false });
            setServerTrigger(!serverTrigger);
        } else {
            ToastMsg('error', response?.message || 'Failed to approve the request');
        }
        setApproveLoading(false);
    };

    // Reimbursement-only — marks an Approved/Partial reimbursement as Processed,
    // signalling that payment/accounting has been completed.
    const handleMarkProcessed = async () => {
        if (!selectedRequest) return;
        setProcessedLoading(true);
        try {
            const payload = {
                id: selectedRequest.id,
                approval_status: 'processed',
                approval_notes: processedRemarks || '',
            };
            const response = await update(REIMBURESMENT_REQUEST_URL, payload);
            if (response?.status === 'success') {
                ToastMsg('success', response.message || 'Reimbursement marked as processed');
                setShowProcessedDialog(false);
                setShowApproveDialog(false);
                setProcessedRemarks('');
                setServerTrigger(!serverTrigger);
            } else {
                ToastMsg('error', response?.message || 'Failed to mark reimbursement as processed');
            }
        } catch (err) {
            ToastMsg('error', err?.message || 'Failed to mark reimbursement as processed');
        } finally {
            setProcessedLoading(false);
        }
    };

    const handleRevertApproval = async () => {
        setRevertLoading(true);
        let payload;

        if (selectedRequest.leave_request_type || selectedRequest.leave_type_id) {
            // Leave request revert payload
            payload = {
                ...selectedRequest,
                approval_notes: revertReason,
                approval_status: "cancelled",
                request_type: "cancel request",
                company: String(selectedRequest.company) || null,
                leave_type: selectedRequest.leave_type_id || null,
            }
            delete payload.leave_type_id;
            delete payload.employee;
        } else if (selectedRequest.reimbursements_type) {
            // Reimbursement request revert payload
            payload = {
                approval_notes: revertReason,
                approval_status: "cancelled",
                id: selectedRequest.id,
                request_type: "cancel request",
                requested_amount: selectedRequest.requested_amount
            }
        } else if (selectedRequest.approval_type === 'Overtime') {
            payload = {
                approval_notes: revertReason,
                approval_status: "cancelled",
                id: selectedRequest.id,
                request_type: "cancel request",
            }
        } else {
            // Advance salary request revert payload
            payload = {
                emi_period: selectedRequest.emi_period,
                approved_amount: selectedRequest.approved_amount,
                reason: selectedRequest.reason,
                approval_notes: revertReason,
                advance_requested: selectedRequest.advance_requested,
                debit_from: selectedRequest.debit_from,
                requested_emi_period: selectedRequest.requested_emi_period,
                uploaded_documents: selectedRequest.uploaded_documents || [],
                id: selectedRequest.id,
                request_type: "advance request",
                approval_status: "cancelled"
            }
        }

        const response = await update(tableData.url, payload);

        if (response.status === 'success') {
            ToastMsg('success', 'Request reverted successfully');
            setShowRevertDialog(false);
            setRevertReason('');
            setServerTrigger(!serverTrigger);
        } else {
            ToastMsg('error', response.message || 'Failed to revert the request');
        }
        setRevertLoading(false);
    };

    const handleConfirmReject = async () => {
        if (payrollLocked && selectedRequestForReject?.approval_type === 'Overtime') {
            ToastMsg('error', 'Overtime rejections are blocked while payroll is locked');
            return;
        }
        // All reject flows require a reason; reimbursement reject is enforced both here and
        // on the backend so the rejected reason ends up in the approval log.
        if (!revertReason || !revertReason.trim()) {
            ToastMsg('error', 'Please provide a reason for rejection');
            return;
        }
        setRejectLoading(true);
        let payload;

        if (tableData.type === 'resignation') {
            // Resignation reject payload — backend requires the level-specific approver
            // ID + a non-empty comment to mark the row rejected.
            const level = parseInt(selectedRequestForReject.level, 10) || 1;
            const isFinalLevel = level === 2;
            const approverId =
                selectedRequestForReject.approved_by?.id ??
                selectedRequestForReject.approved_by ??
                null;
            const finalApproverId =
                selectedRequestForReject.final_approved_by?.id ??
                selectedRequestForReject.final_approved_by ??
                null;
            payload = {
                id: selectedRequestForReject.id,
                level,
                approval_status: 'rejected',
            };
            if (isFinalLevel) {
                payload.final_approved_by = finalApproverId;
                payload.final_approval_date = dayjs().format('YYYY-MM-DD');
                payload.final_approval_comments = revertReason || 'Rejected';
            } else {
                payload.approved_by = approverId;
                payload.approval_date = dayjs().format('YYYY-MM-DD');
                payload.approval_comments = revertReason || 'Rejected';
            }
        } else if (selectedRequestForReject.leave_request_type || selectedRequestForReject.leave_type_id) {
            // Leave request reject payload
            payload = {
                ...selectedRequestForReject,
                approval_notes: revertReason,
                approval_status: "rejected",
                request_type: "leave request",
                company: String(selectedRequestForReject.company) || null,
                leave_type: selectedRequestForReject.leave_type_id || null,
            }
            delete payload.leave_type_id;
            delete payload.employee;
        } else if (selectedRequestForReject.reimbursements_type) {
            // Reimbursement request reject payload — approval_notes carries the reject reason.
            payload = {
                reimbursement_type: selectedRequestForReject.reimbursements_type?.id || selectedRequestForReject.reimbursements_type,
                description: selectedRequestForReject.description,
                requested_amount: selectedRequestForReject.requested_amount,
                approved_amount: selectedRequestForReject.approved_amount,
                uploaded_documents: selectedRequestForReject.uploaded_documents || [],
                id: selectedRequestForReject.id,
                request_type: "reimbursement request",
                approval_status: "rejected",
                approval_notes: revertReason,
            }
        } else if (selectedRequestForReject.approval_type === 'Overtime') {
            payload = {
                id: selectedRequestForReject.id,
                approval_status: 'rejected',
                approval_notes: revertReason,
                request_type: 'overtime request',
            };
        } else {
            // Advance salary request reject payload
            payload = {
                emi_period: selectedRequestForReject.emi_period,
                approved_amount: selectedRequestForReject.approved_amount,
                reason: selectedRequestForReject.reason,
                advance_requested: selectedRequestForReject.advance_requested,
                debit_from: selectedRequestForReject.debit_from,
                requested_emi_period: selectedRequestForReject.requested_emi_period,
                uploaded_documents: selectedRequestForReject.uploaded_documents || [],
                id: selectedRequestForReject.id,
                request_type: "advance request",
                approval_status: "rejected",
                approval_notes: revertReason,
            }
        }

        const response = await update(tableData.url, payload);

        if (response.status === 'success') {
            ToastMsg('success', response.message);
            setShowRejectDialog(false);
            setSelectedRequestForReject(null);
            setRevertReason('');
            setServerTrigger(!serverTrigger);
        } else {
            ToastMsg('error', response.message || 'Failed to reject the request');
        }
        setRejectLoading(false);
    };

    const formatTimeToAMPM = (timeString) => {
        if (!timeString) return '';
        try {
            const [hours, minutes] = timeString.split(':');
            const hour = parseInt(hours, 10);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const formattedHour = hour % 12 || 12;
            return `${formattedHour}:${minutes} ${ampm}`;
        } catch (error) {
            return timeString;
        }
    };

    const getRequestTypeDisplay = (type) => {
        const types = {
            'work_from_home': 'Work From Home',
            'permission': 'Permission',
            'leave': 'Leave'
        };
        return types[type] || type;
    };

    // Common status badge component
    const StatusBadge = ({ status }) => {
        const getStatusConfig = (status) => {
            const config = {
                pending: {
                    text: 'Pending',
                    className: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700'
                },
                'cancel inprogress': {
                    text: 'Cancelling',
                    className: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700'
                },
                approved: {
                    text: 'Approved',
                    className: 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-700'
                },
                rejected: {
                    text: 'Rejected',
                    className: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
                },
                cancelled: {
                    text: 'Cancelled',
                    className: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                },
                partial: {
                    text: 'Partial',
                    className: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700'
                },
                processed: {
                    text: 'Processed',
                    className: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
                }
            };

            return config[status] || {
                text: status?.charAt(0).toUpperCase() + status?.slice(1) || 'Pending',
                className: 'bg-sidebar/10 text-sidebar-primary border border-sidebar-primary/30'
            };
        };

        const statusConfig = getStatusConfig(status);

        return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border shadow-sm ${statusConfig.className}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70 shrink-0" />
                {statusConfig.text}
            </span>
        );
    };

    // Common action buttons component
    const ActionButtons = ({ row, onApprove, onReject, onViewLog, onRevert, showRevert = true, type, payrollLocked = false }) => {

        const handleApprove = () => {
            setSelectedRequest(row);
            setShowApproveDialog(true);
        };

        const handleReject = () => {
            setSelectedRequestForReject(row);
            setShowRejectDialog(true);
        };

        const handleApproveLog = () => {
            setSelectedRequest(row);
            setShowApprovalLog(true);
        }

        const handleRevertApproval = () => {
            setSelectedRequest(row);
            setShowRevertDialog(true);
        }

        const handleProcessed = () => {
            setSelectedRequest(row);
            setShowProcessedDialog(true);
        }

        // Check if the request date has passed (for revert button and approve/reject buttons)
        const isRequestDatePassed = () => {
            if (!row.from_date) return false;

            try {
                const requestDate = new Date(row.from_date);
                const today = new Date();
                requestDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);
                return requestDate < today;
            } catch (error) {
                return false;
            }
        };

        // Only disable for leave requests when date has passed
        const isLeaveRequest = row.leave_request_type === 'leave' || row.approval_type === 'Leave';

        const shouldDisableRevert = isRequestDatePassed();

        // Disable approve/reject buttons when status is not pending
        const shouldDisableApproveReject = row.status !== 'pending' && row.status !== 'cancelled' && row.status !== 'cancel inprogress';

        // Additional condition for leave requests: also disable if date has passed
        const shouldDisableLeaveApproveReject = isLeaveRequest && isRequestDatePassed();

        // Final disable condition: either status is not pending OR payroll is locked (overtime tab)
        const finalDisableApproveReject = shouldDisableApproveReject || payrollLocked;

        const revertTitle = shouldDisableRevert
            ? "Cannot revert - Request date has passed"
            : "Revert Approval";

        const approveRejectTitle = payrollLocked
            ? "Cannot approve/reject while payroll is locked"
            : finalDisableApproveReject || shouldDisableLeaveApproveReject
            ? row.status !== 'pending'
                ? "Cannot approve/reject - Request already processed"
                : "Cannot approve/reject - Request date has passed"
            : "";

        return (
            <div className="flex gap-3 items-center justify-end">
                {(row.status === 'pending' || row.status === 'cancel inprogress' ) && (
                    <>
                        <Button
                            variant="ghost"
                            size="xs"
                            onClick={handleApprove}
                            disabled={finalDisableApproveReject}
                            className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${finalDisableApproveReject
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                : 'bg-sidebar-accent text-sidebar-primary border border-sidebar-primary/30 hover:bg-sidebar-primary/15 hover:text-sidebar-primary dark:bg-sidebar-accent/30 dark:border-sidebar-primary/40'
                                }`}
                            title={finalDisableApproveReject ? approveRejectTitle : "Approve Request"}
                        >
                            <ThumbsUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="xs"
                            onClick={handleReject}
                            // disabled={finalDisableApproveReject}
                            className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${finalDisableApproveReject
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                : 'bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 hover:text-rose-700'
                                }`}
                            title={finalDisableApproveReject ? approveRejectTitle : "Reject Request"}
                        >
                            <ThumbsDown className="h-3.5 w-3.5" />
                        </Button>
                    </>
                )}
                {(row.status === 'approved' || row.status === 'rejected' || row.status === 'partial' || row.status === 'processed' || row.status === 'cancel inprogress' || row.status === 'cancelled') && (
                    <>
                        <Button
                            variant="ghost"
                            size="xs"
                            onClick={handleApproveLog}
                            disabled={!row.approval_log || row.approval_log.length === 0}
                            className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${!row.approval_log || row.approval_log.length === 0
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                : 'bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 hover:text-amber-700'
                                }`}
                            title={(!row.approval_log || row.approval_log.length === 0) ? "No approval log available" : "View Approval Log"}
                        >
                            <History className="h-3.5 w-3.5" />
                        </Button>

                        {type === 'reimbursement' && (row.status === 'approved' || row.status === 'partial') && (
                            <Button
                                variant="ghost"
                                size="xs"
                                onClick={handleProcessed}
                                className="h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 bg-sidebar-accent text-sidebar-primary border border-sidebar-primary/30 hover:bg-sidebar-primary/15 hover:text-sidebar-primary dark:bg-sidebar-accent/30 dark:border-sidebar-primary/40"
                                title="Mark as Processed"
                            >
                                <CheckCheck className="h-3.5 w-3.5" />
                            </Button>
                        )}

                        {showRevert && (
                            (type === 'reimbursement'
                                ? row.status === 'processed'
                                : (row.status === 'approved' || row.status === 'rejected' || row.status === 'partial')
                            ) && (
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={!shouldDisableRevert ? handleRevertApproval : handleRevertApproval}
                                    className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${shouldDisableRevert
                                        // ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                        ? 'bg-sidebar/5 text-sidebar-primary border border-sidebar-primary/25 hover:bg-sidebar/15 hover:text-sidebar-primary'
                                        : 'bg-sidebar/5 text-sidebar-primary border border-sidebar-primary/25 hover:bg-sidebar/15 hover:text-sidebar-primary'
                                        }`}
                                    title={revertTitle}
                                >
                                    <Rewind className="h-3.5 w-3.5" />
                                </Button>
                            )
                        )}
                    </>
                )}
            </div>
        );
    };

    // Leave approval columns
    const leaveRequestColumns = [
        {
            accessor: 'employee', Header: 'Employee Name', sortable: true, filterable: true, fixed: true, width: '180px',
            Cell: ({ value, row }) => {
                const record = row;
                let empDetails = {
                    ...record?.request_from,
                    designation_id: record?.request_from?.designation
                        ? {
                            id: record?.request_from.designation.id,
                            designation_name: record?.request_from.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.request_from?.employee_name || "-",
                    id: record?.request_from?.employee_id || "-",
                };
                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails details={empDetails} size="xs" onClick={() => {
                            setSelectedRequest(row);
                            setShowApproveDialog(true);
                        }} />
                        {row?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            }
        },
        {
            accessor: 'leave_request_type', Header: 'Request Type', filterable: true, width: '160px',
            Cell: ({ value }) => {
                if (!value) {
                    return (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
                            Not Mentioned
                        </span>
                    );
                }

                const getRequestTypeConfig = (type) => {
                    const config = {
                        work_from_home: {
                            text: 'Work From Home',
                            className: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-300 dark:border-purple-700',
                            icon: '🏠'
                        },
                        permission: {
                            text: 'Permission',
                            className: 'bg-sidebar/10 dark:bg-sidebar/20 text-sidebar-primary border border-sidebar-primary/30 dark:border-sidebar-primary/40',
                            icon: '⏱️'
                        },
                        leave: {
                            text: 'Leave',
                            className: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700',
                            icon: '🌴'
                        }
                    };

                    const defaultConfig = {
                        text: type.split('_').map(word =>
                            word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' '),
                        className: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
                        icon: '📝'
                    };

                    return config[type] || defaultConfig;
                };

                const typeConfig = getRequestTypeConfig(value);

                return (
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${typeConfig.className}`}>
                        <span className="text-xs">{typeConfig.icon}</span>
                        {typeConfig.text}
                    </span>
                );
            }
        },
        {
            accessor: 'duration',
            Header: 'Duration',
            sortable: true,
            filterable: true,
            filterType: 'range',
            width: '280px',
            Cell: ({ row }) => {
                const startDate = row.from_date || row.startDate;
                const endDate = row.to_date || row.endDate;
                const startTime = row.start_time;
                const endTime = row.end_time;
                const isPermission = row.leave_request_type === 'permission';

                const formatDate = (dateString) => {
                    if (!dateString) return '';
                    try {
                        const [year, month, day] = dateString.split('-');
                        return `${day}-${month}-${year}`;
                    } catch (error) {
                        return dateString; // Return original if parsing fails
                    }
                };

                const formattedStartDate = formatDate(startDate);
                const formattedEndDate = formatDate(endDate);

                // Same day with times
                if (isPermission && startDate === endDate && startTime && endTime) {
                    return (
                        <div className="flex flex-col gap-2">
                            <span className="text-[0.75rem] font-medium text-gray-700 dark:text-gray-300">{formattedStartDate}</span>
                            <div className="flex items-center gap-3">
                                <span className="bg-sidebar/10 dark:bg-sidebar/20 text-sidebar-primary px-2 py-1 rounded-full text-[0.65rem] font-medium border border-sidebar-primary/30 dark:border-sidebar-primary/40">
                                    {formatTimeToAMPM(startTime)}
                                </span>
                                <span className="text-[0.65rem] text-gray-400 dark:text-gray-500">-</span>
                                <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 px-2 py-1 rounded-full text-[0.65rem] font-medium border border-orange-200 dark:border-orange-700">
                                    {formatTimeToAMPM(endTime)}
                                </span>
                            </div>
                        </div>
                    );
                }

                // Multi-day request
                if (startDate && endDate && startDate !== endDate) {
                    return (
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-[0.75rem] font-medium text-gray-700 dark:text-gray-300">{formattedStartDate}</span>
                                {isPermission && startTime && (
                                    <span className="bg-sidebar/10 dark:bg-sidebar/20 text-sidebar-primary px-1.5 py-0.5 rounded text-[0.6rem] font-medium">
                                        {formatTimeToAMPM(startTime)}
                                    </span>
                                )}
                                <span className="text-[0.65rem] text-gray-400 dark:text-gray-500">to</span>
                                <span className="text-[0.75rem] font-medium text-gray-700 dark:text-gray-300">{formattedEndDate}</span>
                                {isPermission && endTime && (
                                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 px-1.5 py-0.5 rounded text-[0.6rem] font-medium">
                                        {formatTimeToAMPM(endTime)}
                                    </span>
                                )}
                            </div>
                            {!isPermission && row.period && row.period !== '-' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border border-purple-300 dark:border-purple-700 w-fit">
                                    <span className="text-xs">📅</span>
                                    {row.period.replace('_', ' ')}
                                </span>
                            )}
                        </div>
                    );
                }

                // Single day
                if (startDate) {
                    return (
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-[0.75rem] font-medium text-gray-700 dark:text-gray-300">{formattedStartDate}</span>
                                {isPermission && startTime && (
                                    <span className="bg-sidebar/10 dark:bg-sidebar/20 text-sidebar-primary px-2 py-1 rounded-full text-[0.75rem] font-medium border border-sidebar-primary/30 dark:border-sidebar-primary/40">
                                        {formatTimeToAMPM(startTime)}
                                    </span>
                                )}
                            </div>
                            {!isPermission && row.period && row.period !== '-' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border border-purple-300 dark:border-purple-700 w-fit">
                                    <span className="text-xs">📅</span>
                                    {row.period.replace('_', ' ')}
                                </span>
                            )}
                        </div>
                    );
                }

                return '-';
            }
        },
        {
            accessor: 'status', Header: 'Status', sortable: true, filterable: true, filterType: 'requestStatus', width: '140px',
            Cell: ({ value }) => <StatusBadge status={value} />
        },
        {
            accessor: 'approved_by', Header: 'Approved By', sortable: true, filterable: true, width: '180px',
            Cell: ({ value, row }) => {
                const record = row;
                if (!record?.approved_by || !record.approved_by.employee_id || record.approved_by.employee_id === null) {
                    return (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
                            Not Approved
                        </span>
                    );
                }

                let empDetails = {
                    ...record?.approved_by,
                    designation_id: record?.approved_by?.designation
                        ? {
                            id: record?.approved_by.designation.id,
                            designation_name: record?.approved_by.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.approved_by?.employee_name || "-",
                    id: record?.approved_by?.employee_id || "-",
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            }
        },
        {
            accessor: 'reason', Header: 'Reason', sortable: false, width: '200px', Cell: ({ value }) => value ? truncateText(value, 20, "300px", "balance") : ""
        },
        {
            Header: 'Action', fixed: true, align: 'right',
            Cell: ({ row }) => <ActionButtons row={row} />
        }
    ];

    // Reimbursement columns
    const reimbursementColumns = [
        {
            accessor: 'employee', Header: 'Employee Name', sortable: true, filterable: true, fixed: true,
            Cell: ({ value, row }) => {
                const record = row;
                let empDetails = {
                    ...record?.request_from,
                    designation_id: record?.request_from?.designation
                        ? {
                            id: record?.request_from.designation.id,
                            designation_name: record?.request_from.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.request_from?.employee_name || "-",
                    id: record?.request_from?.employee_id || "-",
                };
                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails details={empDetails} size="xs" onClick={() => {
                            setSelectedRequest(row);
                            setShowApproveDialog(true);
                        }} />
                        {row?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            }
        },
        { accessor: 'date', Header: 'Date', sortable: true, filterable: true, filterType: 'range', },
        { accessor: 'amount', Header: 'Amount', sortable: true, filterable: true, Cell: ({ value }) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value) },
        { accessor: 'reimbursements_type', Header: 'Category', sortable: true, Cell: ({ value }) => value?.name || '-' },
        {
            accessor: 'request_notes', Header: 'Request Note', sortable: false, Cell: ({ value }) => value ? truncateText(value, 20, "300px", "balance") : "-"
        },
        {
            accessor: 'status', Header: 'Status', sortable: true, filterable: true, filterType: 'requestStatus', width: '140px',
            Cell: ({ value }) => <StatusBadge status={value} />
        },
        {
            Header: 'Action', fixed: true, align: 'right',
            Cell: ({ row }) => <ActionButtons row={row} type="reimbursement" />
        }
    ];

    // Advance Salary columns
    const advanceSalaryColumns = [
        {
            accessor: 'employee', Header: 'Employee Name', sortable: true, filterable: true, fixed: true,
            Cell: ({ value, row }) => {
                const record = row;
                let empDetails = {
                    ...record?.request_from,
                    designation_id: record?.request_from?.designation
                        ? {
                            id: record?.request_from.designation.id,
                            designation_name: record?.request_from.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.request_from?.employee_name || "-",
                    id: record?.request_from?.employee_id || "-",
                };
                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails details={empDetails} size="xs" onClick={() => {
                            setSelectedRequest(row);
                            setShowApproveDialog(true);
                        }} />
                        {row?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            }
        },
        { accessor: 'date', Header: 'Request Date', sortable: true, filterable: true, filterType: 'date', },
        { accessor: 'advance_requested', Header: 'Amount', sortable: true, filterable: true, Cell: ({ value }) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value) },
        {
            accessor: 'reason', Header: 'Reason', sortable: false, Cell: ({ value }) => value ? truncateText(value, 20, "300px", "balance") : ""
        },
        {
            accessor: 'status', Header: 'Status', sortable: true, width: '140px', filterable: true, filterType: 'requestStatus',
            Cell: ({ value }) => <StatusBadge status={value} />
        },
        {
            Header: 'Action', fixed: true, align: 'right',
            Cell: ({ row }) => <ActionButtons row={row} />
        }
    ];

    const overtimeColumns = [
        {
            accessor: 'employee', Header: 'Employee Name', sortable: true, filterable: true, fixed: true,
            Cell: ({ row }) => {
                const empDetails = {
                    ...row?.request_from,
                    designation_id: row?.request_from?.designation
                        ? {
                            id: row?.request_from.designation.id,
                            designation_name: row?.request_from.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: row?.request_from?.employee_name || "-",
                    id: row?.request_from?.employee_id || "-",
                };
                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails details={empDetails} size="xs" onClick={() => {
                            setSelectedRequest(row);
                            setShowApproveDialog(true);
                        }} />
                        {row?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            }
        },
        { accessor: 'date', Header: 'Date', sortable: true, filterable: true, filterType: 'date' },
        {
            accessor: 'detected_over_time', Header: 'Detected OT',
            Cell: ({ value }) => (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                    {formatOvertimeDuration(value)}
                </span>
            ),
        },
        {
            accessor: 'requested_over_time', Header: 'Requested OT',
            Cell: ({ value }) => (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                    {formatOvertimeDuration(value)}
                </span>
            ),
        },
        {
            accessor: 'reason', Header: 'Reason', sortable: false,
            Cell: ({ value }) => value ? truncateText(value, 20, "300px", "balance") : ""
        },
        {
            accessor: 'status', Header: 'Status', sortable: true, width: '140px', filterable: true, filterType: 'requestStatus',
            Cell: ({ value }) => <StatusBadge status={value} />
        },
        {
            Header: 'Action', fixed: true, align: 'right',
            Cell: ({ row }) => <ActionButtons row={row} type="overtime" payrollLocked={payrollLocked} />
        }
    ];
    const resignationrequestColumns = [
        {
            accessor: 'employee', Header: 'Employee Name', sortable: true, filterable: true, fixed: true,
            Cell: ({ value, row }) => {
                const record = row;
                let empDetails = {
                    ...record?.employee,
                    designation_id: record?.employee?.designation
                        ? {
                            id: record?.employee.designation.id,
                            designation_name: record?.employee.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee?.employee_name || "-",
                    id: record?.employee?.employee_id || "-",
                };
                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails details={empDetails} size="xs" onClick={() => {
                            setSelectedRequest(row);
                            setShowApproveDialog(true);
                        }} />
                        {row?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            }
        },
        { accessor: 'request_date', Header: 'Request Date', sortable: true, Cell: ({ value }) => value ? String(value).split('T')[0] : '-' },
        {
            accessor: 'reason', Header: 'Reason', sortable: false, Cell: ({ value }) => value ? truncateText(value, 20, "300px", "balance") : ""
        },
        {
            accessor: 'status', Header: 'Status', sortable: true, width: '140px',
            Cell: ({ value }) => <StatusBadge status={value} />
        },
        {
            Header: 'Action', fixed: true, align: 'right',
            Cell: ({ row }) => {
                const isApproved = row.status === 'approved' || row.approval_status === 'approved';
                return (
                    <div className="flex items-center justify-end gap-2">
                        <ActionButtons row={row} showRevert={false} />
                        {isApproved && (
                            <Button
                                variant="ghost"
                                size="xs"
                                onClick={() => navigate('/employee/resignationrequest/process', {
                                    state: { resignationId: row.id, stepKey: 1 },
                                })}
                                className="h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 bg-sidebar-accent text-sidebar-primary border border-sidebar-primary/30 hover:bg-sidebar-primary/15 hover:text-sidebar-primary dark:bg-sidebar-accent/30 dark:border-sidebar-primary/40"
                                title="Run Exit Process"
                            >
                                <ListChecks className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                );
            },
        }
    ];

    const shiftChangeColumns = [
        {
            accessor: 'employee', Header: 'Employee Name', sortable: true, filterable: true, fixed: true,
            Cell: ({ row }) => {
                const details = row?.employee_details || row?.employee || {};
                return (
                    <EmployeeDetails
                        details={{
                            ...details,
                            first_name: details.employee_name || details.first_name || row?.employee_name || "-",
                            id: details.employee_id || details.id || "-",
                        }}
                        size="xs"
                    />
                );
            },
        },
        {
            accessor: 'date', Header: 'Date', sortable: true,
            Cell: ({ value }) => value ? dayjs(value).format('DD-MM-YYYY') : "-",
        },
        {
            accessor: 'request_type', Header: 'Type', sortable: true,
            Cell: ({ value }) => value === 'swap' ? 'Swap' : 'Change',
        },
        {
            accessor: 'current_shift_name', Header: 'Current shift',
            Cell: ({ value, row }) => value || row?.current_shift?.shift_name || "-",
        },
        {
            accessor: 'requested_shift_name', Header: 'Requested shift',
            Cell: ({ value, row }) => value || row?.requested_shift_name || row?.requested_shift?.shift_name || "-",
        },
        {
            accessor: 'status', Header: 'Status', sortable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            Header: 'Action', fixed: true, align: 'right',
            Cell: ({ row }) => (
                <Button
                    size="sm"
                    className="bg-sidebar text-white hover:bg-sidebar-primary"
                    onClick={() => navigate('/attendance/shift-change-requests')}
                >
                    Review
                </Button>
            ),
        },
    ];

    const tableData = useMemo(() => {
        switch (activeTab) {
            case 'leave':
                return { data: leaveRequests, setData: setLeaveRequests, columns: insertBranchColumn(leaveRequestColumns), url: LEAVE_REQUEST_URL, type: 'leave' };
            case 'reimbursement':
                return { data: reimbursements, setData: setReimbursements, columns: insertBranchColumn(reimbursementColumns), url: REIMBURESMENT_REQUEST_URL, type: 'reimbursement' };
            case 'advance':
                return { data: advanceSalary, setData: setAdvanceSalary, columns: insertBranchColumn(advanceSalaryColumns), url: ADVANCE_REQUEST_URL, type: 'advance' };
            case 'overtime':
                return { data: overtimeRequests, setData: setOvertimeRequests, columns: insertBranchColumn(overtimeColumns), url: OVERTIME_APPROVAL_URL, type: 'overtime' };
            case 'resign':
                return { data: resignationrequest, setData: setResignationrequest, columns: insertBranchColumn(resignationrequestColumns), url: RESIGNATION_REQUEST_URL, type: 'resignation' };
            case 'shiftchange':
                return { data: shiftChanges, setData: setShiftChanges, columns: insertBranchColumn(shiftChangeColumns), url: SHIFT_CHANGE_REQUEST, type: 'shiftchange' };
            default:
                return { data: [], setData: () => { }, columns: [], type: '' };
        }
    }, [activeTab, leaveRequests, reimbursements, advanceSalary, overtimeRequests, resignationrequest, shiftChanges, insertBranchColumn, leaveRequestColumns, reimbursementColumns, advanceSalaryColumns, overtimeColumns, resignationrequestColumns, shiftChangeColumns]);

    // Render appropriate dialog content based on request type
    const renderDialogContent = () => {
        if (!selectedRequest) return null;

        const requestType = tableData.type;

        switch (requestType) {
            case 'leave':
                return renderLeaveRequestDialog();
            case 'reimbursement':
                return renderReimbursementDialog();
            case 'advance':
                return renderAdvanceSalaryDialog();
            case 'overtime':
                return renderOvertimeDialog();
            case 'resignation':
                return renderResignationDialog();
            default:
                return renderDefaultDialog();
        }
    };

    const renderLeaveRequestDialog = () => (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Header Status Bar */}
            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-sidebar/10 to-sidebar/5 dark:from-sidebar/20 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30">
                <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${selectedRequest.status === 'pending' ? 'bg-yellow-500' :
                        selectedRequest.status === 'approved' ? 'bg-green-500' :
                            'bg-red-500'
                        }`}></div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Leave Request Review</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300">Please review all details carefully</p>
                    </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium border ${selectedRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700' :
                    selectedRequest.status === 'approved' ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700' :
                        'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
                    }`}>
                    {selectedRequest.status?.charAt(0).toUpperCase() + selectedRequest.status?.slice(1)}
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

                {/* Column 1: Employee & Request Info */}
                <div className="space-y-4">
                    {/* Employee Card - Blue Theme */}
                    <div className="bg-gradient-to-br from-sidebar/10 to-sidebar/5 dark:from-sidebar/15 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-sidebar-primary rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Employee Information</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Name:</span>
                                <span className="font-semibold text-gray-800 dark:text-white text-right">{truncateText(selectedRequest.request_from?.employee_name || '-', 22, "220px", "balance")}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Employee ID:</span>
                                <span className="font-semibold text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_id || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Department:</span>
                                <span className="font-semibold text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.department?.name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Designation:</span>
                                <span className="font-semibold text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.designation?.name || '-'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Request Details Card - Purple Theme */}
                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/10 dark:to-violet-900/10 rounded-lg border border-purple-200 dark:border-purple-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Request Details</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-300">Type:</span>
                                <span className={`px-2 py-1 rounded text-xs font-medium border ${selectedRequest.leave_request_type === 'work_from_home' ? 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700' :
                                    selectedRequest.leave_request_type === 'permission' ? 'bg-sidebar/10 text-sidebar-primary border-sidebar-primary/30 dark:bg-sidebar/20 dark:text-sidebar-primary dark:border-sidebar-primary/40' :
                                        'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
                                    }`}>
                                    {getRequestTypeDisplay(selectedRequest.leave_request_type)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Leave Type:</span>
                                <span className="font-semibold text-gray-800 dark:text-white">{selectedRequest.leave_type || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Requested On:</span>
                                <span className="font-semibold text-gray-800 dark:text-white">{selectedRequest.requested_date || '-'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Column 2: Duration & Documents */}
                <div className="space-y-4">
                    {/* Duration Card - Green Theme */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 rounded-lg border border-green-200 dark:border-green-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Duration & Timing</h4>
                        </div>
                        {selectedRequest.leave_request_type === 'permission' ? (
                            // Permission: Show time-based layout
                            <div className="grid grid-cols-2 gap-3">
                                {/* Start Time - Blue Accent */}
                                <div className="text-center p-3 bg-gradient-to-b from-sidebar/10 to-sidebar/15 dark:from-sidebar/20 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30">
                                    <div className="flex items-center justify-center gap-1 mb-1">
                                        <div className="w-2 h-2 bg-sidebar-primary rounded-full"></div>
                                        <h5 className={modalFieldLabelClass}>Start Time</h5>
                                    </div>
                                    <div className="text-xs font-semibold text-gray-800 dark:text-white mb-1">
                                        {selectedRequest.from_date}
                                    </div>
                                    {selectedRequest.start_time && selectedRequest.start_time !== '-' && (
                                        <div className="bg-white dark:bg-gray-800 px-2 py-1 rounded border border-sidebar-primary/25 dark:border-sidebar-primary/30">
                                            <span className="text-xs font-medium text-sidebar-primary">
                                                {formatTimeToAMPM(selectedRequest.start_time)}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* End Time - Orange Accent */}
                                <div className="text-center p-3 bg-gradient-to-b from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/30 rounded-lg border border-orange-200 dark:border-orange-600">
                                    <div className="flex items-center justify-center gap-1 mb-1">
                                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                        <h5 className={modalFieldLabelClass}>End Time</h5>
                                    </div>
                                    <div className="text-xs font-semibold text-gray-800 dark:text-white mb-1">
                                        {selectedRequest.to_date}
                                    </div>
                                    {selectedRequest.end_time && selectedRequest.end_time !== '-' && (
                                        <div className="bg-white dark:bg-gray-800 px-2 py-1 rounded border border-orange-200 dark:border-orange-600">
                                            <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                                                {formatTimeToAMPM(selectedRequest.end_time)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            // Non-permission: Show period layout
                            <div className="space-y-3">
                                {/* Date Range */}
                                <div className="grid grid-cols-2 gap-3">
                                    {/* Start Date */}
                                    <div className="text-center p-3 bg-gradient-to-b from-sidebar/10 to-sidebar/15 dark:from-sidebar/20 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <div className="w-2 h-2 bg-sidebar-primary rounded-full"></div>
                                            <h5 className={modalFieldLabelClass}>Start Date</h5>
                                        </div>
                                        <div className="text-xs font-semibold text-gray-800 dark:text-white">
                                            {selectedRequest.from_date}
                                        </div>
                                    </div>

                                    {/* End Date */}
                                    <div className="text-center p-3 bg-gradient-to-b from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/30 rounded-lg border border-orange-200 dark:border-orange-600">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                            <h5 className={modalFieldLabelClass}>End Date</h5>
                                        </div>
                                        <div className="text-xs font-semibold text-gray-800 dark:text-white">
                                            {selectedRequest.to_date}
                                        </div>
                                    </div>
                                </div>

                                {/* Period Type */}
                                {selectedRequest.period && selectedRequest.period !== '-' && (
                                    <div className="text-center p-3 bg-gradient-to-b from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/30 rounded-lg border border-purple-200 dark:border-purple-600">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                            <h5 className={modalFieldLabelClass}>Period Type</h5>
                                        </div>
                                        <div className="text-xs font-semibold text-gray-800 dark:text-white capitalize">
                                            {selectedRequest.period.replace('_', ' ')}
                                        </div>
                                        {selectedRequest.total_duration && selectedRequest.total_duration !== 'NaT' && (
                                            <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                                Duration: {selectedRequest.total_duration}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Documents Card - Indigo Theme */}
                    {selectedRequest.uploaded_documents && selectedRequest.uploaded_documents.length > 0 && (
                        <div className="bg-gradient-to-br from-violet-50 to-violet-100/50 dark:from-violet-900/10 dark:to-violet-900/5 rounded-lg border border-violet-200 dark:border-violet-700/60 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Attached Documents</h4>
                                <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded text-xs">
                                    {selectedRequest.uploaded_documents.length}
                                </span>
                            </div>
                            <div className="space-y-2">
                                {selectedRequest.uploaded_documents.map((docUrl, index) => {
                                    const getFileName = (url) => {
                                        try {
                                            return new URL(url).pathname.split('/').pop() || `Document ${index + 1}`;
                                        } catch {
                                            return `Document ${index + 1}`;
                                        }
                                    };

                                    const isImage = (url) => {
                                        const fileName = getFileName(url);
                                        const extension = fileName.split('.').pop()?.toLowerCase();
                                        return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
                                    };

                                    const fileName = getFileName(docUrl);
                                    const fileIsImage = isImage(docUrl);

                                    return (
                                        <div key={index} className="flex items-center justify-between p-2 bg-white/50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 transition-colors">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <span className="text-base">
                                                    {fileIsImage ? '🖼️' : '📄'}
                                                </span>
                                                <span className="text-xs font-medium text-gray-800 dark:text-white truncate flex-1">
                                                    {fileName}
                                                </span>
                                            </div>
                                            {fileIsImage ? (
                                                <Image
                                                    width={80}
                                                    src={docUrl}
                                                    alt={`Preview of ${fileName}`}
                                                    className="rounded cursor-pointer"
                                                    preview={{
                                                        mask: <Eye className="h-3 w-3" />,
                                                    }}
                                                />
                                            ) : (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        const link = document.createElement('a');
                                                        link.href = docUrl;
                                                        link.download = fileName;
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        document.body.removeChild(link);
                                                    }}
                                                    className="h-6 px-2 text-sidebar-primary hover:bg-sidebar/10 dark:hover:bg-sidebar/20 text-xs font-medium transition-colors"
                                                >
                                                    View
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Column 3: Reason & Notes */}
                <div className="space-y-4">
                    {/* Reason Card - Amber Theme */}
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10 rounded-lg border border-amber-200 dark:border-amber-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Reason</h4>
                        </div>
                        <div className="bg-white/50 dark:bg-gray-800/50 rounded border border-amber-200 dark:border-amber-600 min-h-[100px] max-h-[120px] overflow-y-auto p-3">
                            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                                {selectedRequest.reason || 'No reason provided for this request.'}
                            </p>
                        </div>
                    </div>

                    {/* Approval Notes Card - Gray Theme */}
                    <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-gray-600 dark:bg-gray-400 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Approval Notes</h4>
                            <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                        </div>
                        <Textarea
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.target.value)}
                            placeholder="Add your comments or notes for this approval decision..."
                            rows={3}
                            showCharCount={true}
                            maxLength={250}
                            className={cn(
                                "w-full rounded border transition-colors duration-200 text-xs",
                                "border-input bg-background text-foreground",
                                "focus:border-sidebar-primary focus:ring-1 focus:ring-sidebar-primary/20 dark:focus:border-sidebar-primary dark:focus:ring-sidebar-primary/30",
                            )}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            These notes will be visible to the employee and other approvers.
                        </p>
                    </div>
                </div>
            </div>

            {/* Information Footer - Blue Theme */}
            <div className="bg-gradient-to-r from-sidebar/10 to-sidebar/5 dark:from-sidebar/20 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30 p-3">
                <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-4 h-4 bg-sidebar-primary rounded-full flex items-center justify-center mt-0.5">
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <p className="text-xs font-medium text-sidebar-primary">Important Information</p>
                        <p className="text-xs text-sidebar-primary/90 mt-1">
                            Approving this request will automatically notify the employee and update their leave balance.
                            Rejecting will require providing a reason.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderReimbursementDialog = () => (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Header Status Bar */}
            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-sidebar/10 to-sidebar/5 dark:from-sidebar/20 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30">
                <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${selectedRequest.status === 'pending' ? 'bg-yellow-500' :
                        selectedRequest.status === 'processed' ? 'bg-emerald-500' :
                            selectedRequest.status === 'approved' || selectedRequest.status === 'partial' ? 'bg-green-500' :
                                'bg-red-500'
                        }`}></div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Reimbursement Request Review</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300">Please review all details carefully</p>
                    </div>
                </div>
                <StatusBadge status={selectedRequest.status} />
            </div>

            {/* Dedicated "Mark as Processed" CTA — visible only when the
                reimbursement has been approved/partial and is awaiting
                finance processing. Lives inside the dialog body (not the
                footer) so it doesn't conflict with the Approve save button. */}
            {(selectedRequest.status === 'approved' || selectedRequest.status === 'partial') && (
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-emerald-200 dark:border-emerald-700 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20">
                    <div className="flex items-center gap-2 min-w-0">
                        <CheckCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <div className="min-w-0">
                            <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">
                                Ready for payment processing
                            </p>
                            <p className="text-[11px] text-emerald-700 dark:text-emerald-300 truncate">
                                Approved Rs {selectedRequest.approved_amount ?? selectedRequest.requested_amount ?? 0}.00 — mark as processed once payment / accounting is complete.
                            </p>
                        </div>
                    </div>
                    <Button
                        size="sm"
                        variant="brand"
                        onClick={() => setShowProcessedDialog(true)}
                        className="shrink-0"
                    >
                        <CheckCheck className="w-4 h-4 mr-1" />
                        Mark as Processed
                    </Button>
                </div>
            )}

            {/* Processed receipt summary — once the reimbursement has been
                processed, surface who processed it and when so the reviewer
                doesn't have to open the approval log. */}
            {selectedRequest.status === 'processed' && (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20">
                    <BadgeCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <div className="min-w-0 text-xs text-emerald-800 dark:text-emerald-200">
                        Processed
                        {selectedRequest.processed_by_name ? ` by ${selectedRequest.processed_by_name}` : ''}
                        {selectedRequest.processed_date ? ` on ${dayjs(selectedRequest.processed_date).format('DD MMM YYYY, hh:mm A')}` : ''}
                        {selectedRequest.processed_remarks ? ` — ${selectedRequest.processed_remarks}` : ''}
                    </div>
                </div>
            )}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Employee & Request Info */}
                <div className="space-y-4">
                    <div className="bg-gradient-to-br from-sidebar/10 to-sidebar/5 dark:from-sidebar/15 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-sidebar-primary rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Employee Information</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Name:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Employee ID:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_id || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Department:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.department?.name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Designation:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.designation?.name || '-'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/10 dark:to-violet-900/10 rounded-lg border border-purple-200 dark:border-purple-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Reimbursement Details</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Category:</span>
                                <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.reimbursements_type?.name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Requested Amount:</span>
                                <span className="font-medium text-gray-800 dark:text-white">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(selectedRequest.requested_amount ?? selectedRequest.amount ?? 0)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Date:</span>
                                <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.date || '-'}</span>
                            </div>
                            {selectedRequest.reimbursements_type?.expense_category === 'travel' && (
                                <>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">From:</span>
                                        <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.from_location || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">To:</span>
                                        <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.to_location || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">Mode:</span>
                                        <span className="font-medium text-gray-800 dark:text-white capitalize">{(selectedRequest.mode_of_transport || '-').replace('_', ' ')}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">Distance:</span>
                                        <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.distance_km ? `${selectedRequest.distance_km} km` : '-'}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Approved Amount input — defaults to requested. Lowering it triggers Partial status. */}
                    {selectedRequest.status === 'pending' && selectedRequest.request_type !== 'cancel request' && (
                        <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/10 dark:to-green-900/10 rounded-lg border border-emerald-200 dark:border-emerald-700 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Approved Amount</h4>
                            </div>
                            <Input
                                type="number"
                                min={0}
                                max={selectedRequest.requested_amount}
                                value={reimbursementApprovedAmount}
                                onChange={(e) => setReimbursementApprovedAmount(e.target.value)}
                                placeholder="Enter approved amount"
                                className="w-full"
                            />
                            {(() => {
                                const requested = Number(selectedRequest.requested_amount || 0);
                                const approvedNum = Number(reimbursementApprovedAmount);
                                if (!Number.isFinite(approvedNum) || approvedNum <= 0) {
                                    return <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Defaults to the requested amount. Lower it to approve partially.</p>;
                                }
                                if (approvedNum < requested) {
                                    return <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">This will be marked as <strong>Partial</strong> approval.</p>;
                                }
                                if (approvedNum > requested) {
                                    return <p className="text-xs text-red-600 dark:text-red-400 mt-2">Approved amount cannot exceed the requested amount.</p>;
                                }
                                return <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-2">Full amount will be approved.</p>;
                            })()}
                        </div>
                    )}

                    {/* attachement preview in a carosel format */}
                    {selectedRequest.uploaded_documents && selectedRequest.uploaded_documents.length > 0 && (
                        <div className="bg-gradient-to-br from-violet-50 to-violet-100/50 dark:from-violet-900/10 dark:to-violet-900/5 rounded-xl border border-violet-200 dark:border-violet-700/60 p-4 shadow-lg">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-gradient-to-r from-violet-500 to-violet-600 rounded-full"></div>
                                    <h4 className="text-sm font-semibold text-gray-800 dark:text-white">Attached Documents</h4>
                                    <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full text-xs font-medium">
                                        {selectedRequest.uploaded_documents.length}
                                    </span>
                                </div>
                            </div>

                            <div className="relative">
                                {/* Navigation Arrows */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const container = e.currentTarget.closest('.relative').querySelector('.carousel-container');
                                        container.scrollBy({ left: -300, behavior: 'smooth' });
                                    }}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full w-8 h-8 flex items-center justify-center shadow-md hover:shadow-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                                </button>

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const container = e.currentTarget.closest('.relative').querySelector('.carousel-container');
                                        container.scrollBy({ left: 300, behavior: 'smooth' });
                                    }}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full w-8 h-8 flex items-center justify-center shadow-md hover:shadow-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                                </button>

                                {/* Carousel Container */}
                                <div className="carousel-container flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth pb-3 px-1" style={{ scrollbarWidth: 'none' }}>
                                    {selectedRequest.uploaded_documents.map((docUrl, index) => {
                                        const getFileName = (url) => {
                                            try {
                                                return new URL(url).pathname.split('/').pop() || `Document ${index + 1}`;
                                            } catch {
                                                return `Document ${index + 1}`;
                                            }
                                        };

                                        const isImage = (url) => {
                                            const fileName = getFileName(url);
                                            const extension = fileName.split('.').pop()?.toLowerCase();
                                            return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension);
                                        };

                                        const fileName = getFileName(docUrl);
                                        const fileIsImage = isImage(docUrl);
                                        const isPDF = fileName.toLowerCase().endsWith('.pdf');

                                        const handleDownload = (e) => {
                                            e.stopPropagation();
                                            const link = document.createElement('a');
                                            link.href = docUrl;
                                            link.download = fileName;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        };

                                        return (
                                            <div
                                                key={index}
                                                className="flex-none w-56 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:border-sidebar-primary/40 dark:hover:border-sidebar-primary/50 overflow-hidden group"
                                            >
                                                {/* File Preview Area */}
                                                <div className="h-32 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center relative overflow-hidden">
                                                    {fileIsImage ? (
                                                        <div className="relative w-full h-full">
                                                            <Image
                                                                src={docUrl}
                                                                alt={`Preview of ${fileName}`}
                                                                fill
                                                                className="object-cover group-hover:scale-105 transition-transform duration-300"
                                                                sizes="(max-width: 224px) 100vw, 224px"
                                                            />
                                                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        </div>
                                                    ) : (
                                                        <div className="p-4 text-center">
                                                            <div className="w-12 h-12 mx-auto mb-2 rounded-lg bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center shadow-sm">
                                                                <span className="text-2xl">
                                                                    {isPDF ? '📕' : '📄'}
                                                                </span>
                                                            </div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                                                {isPDF ? 'PDF Document' : 'File'}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Preview Overlay with Download Button */}
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <div className="flex gap-2">
                                                            {fileIsImage ? (
                                                                <>
                                                                    {/* Image Preview Button */}
                                                                    <Image
                                                                        width={200}
                                                                        src={docUrl}
                                                                        alt={fileName}
                                                                        className="hidden"
                                                                        preview={{
                                                                            mask: (
                                                                                <Button
                                                                                    type="text"
                                                                                    className="text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm border-0 flex items-center gap-2 px-4 py-2 rounded-lg"
                                                                                >
                                                                                    <Eye className="w-4 h-4" />
                                                                                </Button>
                                                                            ),
                                                                        }}
                                                                    />
                                                                </>
                                                            ) : (
                                                                <Button
                                                                    type="text"
                                                                    className="text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm border-0 flex items-center gap-2 px-4 py-2 rounded-lg"
                                                                    icon={<Eye className="w-4 h-4" />}
                                                                    onClick={handleDownload}
                                                                >
                                                                    <span className="text-sm font-medium">View</span>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* File Info with Download Button */}
                                                <div className="p-3">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-1.5 mb-1">
                                                                <span className="text-xs">
                                                                    {fileIsImage ? '🖼️' : (isPDF ? '📕' : '📄')}
                                                                </span>
                                                                <span className="text-xs font-medium text-gray-800 dark:text-white truncate">
                                                                    {fileName}
                                                                </span>
                                                            </div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                                {fileIsImage ? 'Image' : isPDF ? 'PDF Document' : 'Document'}
                                                            </div>
                                                        </div>
                                                        {/* Download Button for Images */}
                                                        {
                                                            fileIsImage && (
                                                                <Button
                                                                    type="text"
                                                                    variant='ghost'
                                                                    onClick={handleDownload}
                                                                >
                                                                    <Download className="w-4 h-4" />
                                                                </Button>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Carousel Dots Indicator */}
                                {selectedRequest.uploaded_documents.length > 1 && (
                                    <div className="flex justify-center gap-1.5 mt-4">
                                        {[...Array(Math.min(5, selectedRequest.uploaded_documents.length))].map((_, i) => (
                                            <button
                                                key={i}
                                                className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                                                onClick={(e) => {
                                                    const container = e.currentTarget.closest('.relative').querySelector('.carousel-container');
                                                    container.scrollTo({ left: i * 300, behavior: 'smooth' });
                                                }}
                                            />
                                        ))}
                                        {selectedRequest.uploaded_documents.length > 5 && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                                                +{selectedRequest.uploaded_documents.length - 5}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>


                {/* Reason & Notes */}
                <div className="space-y-4">
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10 rounded-lg border border-amber-200 dark:border-amber-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Request Notes</h4>
                        </div>
                        <div className="bg-white/50 dark:bg-gray-800/50 rounded border border-amber-200 dark:border-amber-600 min-h-[100px] max-h-[120px] overflow-y-auto p-3">
                            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                                {selectedRequest.request_notes || 'No notes provided for this request.'}
                            </p>
                        </div>
                    </div>

                    {/* Approval Notes Card */}
                    <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-gray-600 dark:bg-gray-400 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Approval Notes</h4>
                            <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                        </div>
                        <Textarea
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.target.value)}
                            placeholder="Add your comments or notes for this approval decision..."
                            rows={3}
                            showCharCount={true}
                            maxLength={250}
                            className={cn(
                                "w-full rounded border transition-colors duration-200 text-xs",
                                "border-input bg-background text-foreground",
                                "focus:border-sidebar-primary focus:ring-1 focus:ring-sidebar-primary/20 dark:focus:border-sidebar-primary dark:focus:ring-sidebar-primary/30",
                            )}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            These notes will be visible to the employee and other approvers.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const formatOvertimeDuration = (value) => {
        if (!value) return '-';
        const parts = String(value).split(':');
        if (parts.length !== 3) return value;
        const [hours, minutes, seconds] = parts.map((part) => parseInt(part, 10) || 0);
        if (hours > 0) return `${hours}h ${minutes}m`;
        if (minutes > 0) return `${minutes}m ${seconds}s`;
        return `${seconds}s`;
    };

    const parseOvertimeSeconds = (value) => {
        if (!value) return 0;
        const parts = String(value).split(':');
        if (parts.length !== 3) return null;
        const [hours, minutes, seconds] = parts.map((part) => parseInt(part, 10) || 0);
        return (hours * 3600) + (minutes * 60) + seconds;
    };

    const formatDialogDate = (value) => {
        if (!value) return '-';
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.format('DD-MM-YYYY') : value;
    };

    const formatDialogDateTime = (value) => {
        if (!value) return '-';
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.format('DD-MM-YYYY hh:mm A') : value;
    };

    const OvertimeChip = ({ label, value, tone = 'orange' }) => {
        const tones = {
            orange: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-200 dark:border-orange-700',
            amber: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700',
            teal: 'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/30 dark:text-teal-200 dark:border-teal-700',
        };
        return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${tones[tone] || tones.orange}`}>
                <span className="font-normal opacity-80">{label}</span>
                <span>{value}</span>
            </span>
        );
    };

    const renderOvertimeDialog = () => (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg border border-orange-200 dark:border-orange-700">
                <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${selectedRequest.status === 'pending' ? 'bg-yellow-500' :
                        selectedRequest.status === 'approved' ? 'bg-green-500' :
                            selectedRequest.status === 'rejected' ? 'bg-red-500' :
                                'bg-gray-400'
                        }`} />
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Overtime Request Review</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300">Review detected and requested overtime before approval</p>
                    </div>
                </div>
                <StatusBadge status={selectedRequest.status} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-4">
                    <div className="bg-gradient-to-br from-sidebar/10 to-sidebar/5 dark:from-sidebar/15 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-sidebar-primary rounded-full" />
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Employee Information</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between gap-4">
                                <span className="text-gray-600 dark:text-gray-300">Name</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_name || '-'}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="text-gray-600 dark:text-gray-300">Employee ID</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_id || '-'}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="text-gray-600 dark:text-gray-300">Department</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.department?.name || '-'}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="text-gray-600 dark:text-gray-300">Designation</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.designation?.name || '-'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/10 dark:to-amber-900/10 rounded-lg border border-orange-200 dark:border-orange-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-orange-500 rounded-full" />
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Attendance & Overtime</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between gap-4">
                                <span className="text-gray-600 dark:text-gray-300">Date</span>
                                <span className="font-medium text-gray-800 dark:text-white">{formatDialogDate(selectedRequest.date)}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="text-gray-600 dark:text-gray-300">Check In</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{formatDialogDateTime(selectedRequest.check_in)}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="text-gray-600 dark:text-gray-300">Check Out</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{formatDialogDateTime(selectedRequest.check_out)}</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-4">
                            <OvertimeChip
                                label="Detected"
                                value={formatOvertimeDuration(selectedRequest.detected_over_time)}
                                tone="orange"
                            />
                            <OvertimeChip
                                label="Requested"
                                value={formatOvertimeDuration(selectedRequest.requested_over_time)}
                                tone="amber"
                            />
                            {selectedRequest.approved_over_time && (
                                <OvertimeChip
                                    label="Approved"
                                    value={formatOvertimeDuration(selectedRequest.approved_over_time)}
                                    tone="teal"
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10 rounded-lg border border-amber-200 dark:border-amber-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-amber-500 rounded-full" />
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Reason</h4>
                        </div>
                        <div className="bg-white/50 dark:bg-gray-800/50 rounded border border-amber-200 dark:border-amber-600 min-h-[88px] max-h-[120px] overflow-y-auto p-3">
                            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                                {selectedRequest.reason || 'No reason provided for this request.'}
                            </p>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/10 dark:to-emerald-900/10 rounded-lg border border-teal-200 dark:border-teal-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-teal-500 rounded-full" />
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Approved Overtime</h4>
                        </div>
                        <Input
                            value={overtimeApprovedHours}
                            onChange={(e) => setOvertimeApprovedHours(e.target.value)}
                            placeholder="HH:MM:SS"
                            className="text-sm"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Defaults to requested hours. Format: HH:MM:SS (e.g. 02:44:23)
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-gray-600 dark:bg-gray-400 rounded-full" />
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Approval Notes</h4>
                            <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                        </div>
                        <Textarea
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.target.value)}
                            placeholder="Add your comments or notes for this approval decision..."
                            rows={3}
                            showCharCount
                            maxLength={250}
                            className={cn(
                                "w-full rounded border transition-colors duration-200 text-xs",
                                "border-input bg-background text-foreground",
                                "focus:border-sidebar-primary focus:ring-1 focus:ring-sidebar-primary/20 dark:focus:border-sidebar-primary dark:focus:ring-sidebar-primary/30"
                            )}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderAdvanceSalaryDialog = () => (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Header Status Bar */}
            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-sidebar/10 to-sidebar/5 dark:from-sidebar/20 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30">
                <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${selectedRequest.status === 'pending' ? 'bg-yellow-500' :
                        selectedRequest.status === 'approved' ? 'bg-green-500' :
                            'bg-red-500'
                        }`}></div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Advance Salary Request Review</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300">Please review all details carefully</p>
                    </div>
                </div>
                <StatusBadge status={selectedRequest.status} />
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Employee & Request Info */}
                <div className="space-y-4">
                    <div className="bg-gradient-to-br from-sidebar/10 to-sidebar/5 dark:from-sidebar/15 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-sidebar-primary rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Employee Information</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Name:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Employee ID:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_id || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Department:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.department?.name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Designation:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.designation?.name || '-'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/10 dark:to-violet-900/10 rounded-lg border border-purple-200 dark:border-purple-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Advance Details</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Amount Requested:</span>
                                <span className="font-medium text-gray-800 dark:text-white">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(selectedRequest.advance_requested)}</span>
                            </div>
                            {selectedRequest.status === 'pending' && selectedRequest.request_type !== 'cancel request' && (
                                <div className="pt-2">
                                    <label className="block text-xs font-semibold text-gray-800 dark:text-white mb-2">
                                        Approved Amount <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        type="number"
                                        min={0}
                                        max={selectedRequest.advance_requested}
                                        value={advanceApprovedAmount}
                                        onChange={(e) => setAdvanceApprovedAmount(e.target.value)}
                                        placeholder="Enter approved amount"
                                        className="w-full"
                                    />
                                    {Number(advanceApprovedAmount) > 0 && Number(advanceApprovedAmount) < Number(selectedRequest.advance_requested) && (
                                        <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">Approving less than requested.</p>
                                    )}
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Request Date:</span>
                                <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.date || '-'}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-300">Requested EMI:</span>
                                <span className="font-medium text-gray-800 dark:text-white">
                                    {(selectedRequest.requested_emi_period ?? selectedRequest.emi_period) || '-'} months
                                </span>
                            </div>
                        </div>
                        <div className="mt-3 space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-800 dark:text-white mb-2">
                                    Approved EMI Period <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    size="large"
                                    value={approvedEmiPeriod ? Number(approvedEmiPeriod) : undefined}
                                    onChange={(value) => setApprovedEmiPeriod(String(value))}
                                    placeholder="Select EMI period"
                                    className="w-full"
                                    options={Array.from({ length: 12 }, (_, i) => ({
                                        value: i + 1,
                                        label: `${i + 1} Month${i + 1 > 1 ? 's' : ''}`,
                                    }))}
                                />
                                {(advanceApprovedAmount || selectedRequest.advance_requested) && approvedEmiPeriod ? (
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                        Monthly EMI: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(
                                            Math.round(Number(advanceApprovedAmount || selectedRequest.advance_requested) / Number(approvedEmiPeriod))
                                        )}
                                    </p>
                                ) : null}
                            </div>
                            <div>
                            <label className="block text-xs font-semibold text-gray-800 dark:text-white mb-2">Debit From Month <span className="text-red-500">*</span></label>
                            <DatePicker
                                picker="month"
                                value={debitFrom ? dayjs(`${debitFrom}-01`) : null}
                                onChange={(date) => setDebitFrom(date ? date.format('YYYY-MM') : '')}
                                disabledDate={(current) => current && current.isBefore(dayjs().startOf('month'), 'month')}
                                placeholder="Select debit from month"
                                className={cn(
                                    "w-full rounded border transition-colors duration-200 text-xs px-3 py-2",
                                    "border-input bg-background text-foreground",
                                    "focus:border-sidebar-primary focus:ring-1 focus:ring-sidebar-primary/20 dark:focus:border-sidebar-primary dark:focus:ring-sidebar-primary/30",
                                )}
                            />
                            {debitFrom && approvedEmiPeriod ? (
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                    EMI schedule: {Array.from({ length: Number(approvedEmiPeriod) }, (_, i) =>
                                        dayjs(`${debitFrom}-01`).add(i, 'month').format('MMM YYYY')
                                    ).join(' → ')}
                                </p>
                            ) : null}
                            </div>
                        </div>
                    </div>

                </div>

                {/* Reason & Notes */}
                <div className="space-y-4">
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10 rounded-lg border border-amber-200 dark:border-amber-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Reason</h4>
                        </div>
                        <div className="bg-white/50 dark:bg-gray-800/50 rounded border border-amber-200 dark:border-amber-600 min-h-[100px] max-h-[120px] overflow-y-auto p-3">
                            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                                {selectedRequest.reason || 'No reason provided for this request.'}
                            </p>
                        </div>
                    </div>

                    {Array.isArray(selectedRequest.existing_advances) && selectedRequest.existing_advances.length > 0 && (
                        <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/10 dark:to-blue-900/10 rounded-lg border border-sky-200 dark:border-sky-700 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-sky-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Existing advances</h4>
                            </div>
                            <div className="space-y-2 text-xs">
                                {selectedRequest.existing_advances.map((adv) => (
                                    <div key={adv.id} className="flex justify-between gap-2 text-gray-700 dark:text-gray-300">
                                        <span>₹{(adv.advance_approved || 0).toLocaleString()} · EMI ₹{(adv.emi_amount || 0).toLocaleString()}/mo</span>
                                        <span>Due ₹{(adv.outstanding_balance || 0).toLocaleString()}</span>
                                    </div>
                                ))}
                                {approvedEmiPeriod && (advanceApprovedAmount || selectedRequest.advance_requested) && (
                                    <p className="text-[11px] text-sky-800 dark:text-sky-300 pt-1 border-t border-sky-200 dark:border-sky-800">
                                        Total monthly deduction if approved:{' '}
                                        {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(
                                            selectedRequest.existing_advances.reduce((sum, adv) => sum + Number(adv.emi_amount || 0), 0)
                                            + Math.round(Number(advanceApprovedAmount || selectedRequest.advance_requested) / Number(approvedEmiPeriod))
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Approval Notes Card */}
                    <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-gray-600 dark:bg-gray-400 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Approval Notes</h4>
                            <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                        </div>
                        <Textarea
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.target.value)}
                            placeholder="Add your comments or notes for this approval decision..."
                            rows={3}
                            showCharCount={true}
                            maxLength={250}
                            className={cn(
                                "w-full rounded border transition-colors duration-200 text-xs",
                                "border-input bg-background text-foreground",
                                "focus:border-sidebar-primary focus:ring-1 focus:ring-sidebar-primary/20 dark:focus:border-sidebar-primary dark:focus:ring-sidebar-primary/30",
                            )}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            These notes will be visible to the employee and other approvers.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderResignationDialog = () => {
        const isFinalLevel = String(selectedRequest.level) === '2';

        const onNoticePeriodChange = (val) => {
            const numeric = val.replace(/[^0-9]/g, '');
            const computed = numeric === '' ? null : dayjs().add(parseInt(numeric, 10), 'day');
            setResignationApproval((prev) => ({
                ...prev,
                notice_period: numeric,
                last_working_day: computed,
            }));
        };

        const onLwdChange = (date) => {
            const days = date ? Math.max(0, date.diff(dayjs().startOf('day'), 'day')) : '';
            setResignationApproval((prev) => ({
                ...prev,
                last_working_day: date,
                notice_period: days === '' ? '' : String(days),
            }));
        };

        return (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                {/* Header Status Bar */}
                <div className="flex items-center justify-between p-2 bg-gradient-to-r from-sidebar/10 to-sidebar/5 dark:from-sidebar/20 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30">
                    <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${selectedRequest.status === 'pending' ? 'bg-yellow-500' :
                            selectedRequest.status === 'approved' ? 'bg-green-500' :
                                'bg-red-500'
                            }`}></div>
                        <div>
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                                Resignation Request Review {isFinalLevel && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-purple-100 text-purple-700 border border-purple-200">Final Approval</span>}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-300">Please review and capture the notice period before approving</p>
                        </div>
                    </div>
                    <StatusBadge status={selectedRequest.status} />
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Employee & Request Info */}
                    <div className="space-y-4">
                        <div className="bg-gradient-to-br from-sidebar/10 to-sidebar/5 dark:from-sidebar/15 dark:to-sidebar/10 rounded-lg border border-sidebar-primary/25 dark:border-sidebar-primary/30 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-sidebar-primary rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Employee Information</h4>
                            </div>
                            <div className="space-y-2 text-xs">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Name:</span>
                                    <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.employee?.employee_name || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Employee ID:</span>
                                    <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.employee?.employee_id || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Department:</span>
                                    <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.employee?.department?.name || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Designation:</span>
                                    <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.employee?.designation?.name || '-'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/10 dark:to-violet-900/10 rounded-lg border border-purple-200 dark:border-purple-700 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Resignation Details</h4>
                            </div>
                            <div className="space-y-2 text-xs">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Request Date:</span>
                                    <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.request_date?.split('T')[0] || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Existing Notice Period:</span>
                                    <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.notice_period || '-'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10 rounded-lg border border-amber-200 dark:border-amber-700 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Reason</h4>
                            </div>
                            <div className="bg-white/50 dark:bg-gray-800/50 rounded border border-amber-200 dark:border-amber-600 min-h-[100px] max-h-[120px] overflow-y-auto p-3">
                                <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                                    {selectedRequest.reason || 'No reason provided for this request.'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Approval form */}
                    <div className="space-y-4">
                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/10 dark:to-teal-900/10 rounded-lg border border-emerald-200 dark:border-emerald-700 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">
                                    {isFinalLevel ? 'Final Approval' : 'Approval'}
                                </h4>
                            </div>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Notice Period (days) <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            type="text"
                                            value={resignationApproval.notice_period}
                                            onChange={(e) => onNoticePeriodChange(e.target.value)}
                                            placeholder="e.g. 30"
                                            maxLength={3}
                                            className="w-full"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Last Working Day <span className="text-red-500">*</span>
                                        </label>
                                        <DatePicker
                                            value={resignationApproval.last_working_day ? dayjs(resignationApproval.last_working_day) : null}
                                            onChange={onLwdChange}
                                            format="DD-MM-YYYY"
                                            className="w-full"
                                            disabledDate={(d) => d && d < dayjs().startOf('day')}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Approval Comments <span className="text-red-500">*</span>
                                    </label>
                                    <Textarea
                                        value={resignationApproval.approval_comments}
                                        onChange={(e) =>
                                            setResignationApproval((prev) => ({ ...prev, approval_comments: e.target.value }))
                                        }
                                        placeholder="Comments visible to the employee and other approvers..."
                                        rows={4}
                                        showCharCount={true}
                                        maxLength={150}
                                        className="w-full"
                                    />
                                </div>

                                {(() => {
                                    const obligations = selectedRequest.salary_obligations || {};
                                    const items = obligations.items || [];
                                    return (
                                        <div className="space-y-2">
                                            {obligations.is_payroll_hold && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-rose-100 text-rose-700 border border-rose-200">
                                                    Already on payroll hold
                                                </span>
                                            )}
                                            {items.length > 0 && (
                                                <div className="rounded border border-amber-200 dark:border-amber-700 bg-white/70 dark:bg-gray-800/40 p-2">
                                                    <p className="text-[11px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                                                        Open salary items
                                                    </p>
                                                    <ul className="space-y-1 text-[11px] text-gray-600 dark:text-gray-300">
                                                        {items.map((item) => (
                                                            <li key={`${item.item_code}-${item.source_id}`}>
                                                                {item.label}
                                                                {item.amount != null ? ` · ${item.amount}` : ""}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                                                <input
                                                    type="checkbox"
                                                    checked={!!resignationApproval.is_payroll_hold}
                                                    onChange={(e) =>
                                                        setResignationApproval((prev) => ({
                                                            ...prev,
                                                            is_payroll_hold: e.target.checked,
                                                        }))
                                                    }
                                                />
                                                Put payroll on hold
                                            </label>
                                            <p className="text-[11px] text-gray-500">
                                                Hold skips the next payroll run. Unchecking here does not release an existing hold.
                                            </p>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        <div className={cn(
                            "p-3 rounded-lg border text-xs",
                            "bg-sidebar/10 border-sidebar-primary/25 text-sidebar-primary",
                            "dark:bg-sidebar/20 dark:border-sidebar-primary/30 dark:text-sidebar-primary"
                        )}>
                            {isFinalLevel
                                ? 'You are the final approver. Approving will move the resignation into the exit process (attendance, audit, assets handover, and final verification).'
                                : 'Approving will forward this resignation to the final approver. Both approvers must approve before the exit process can start.'}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderDefaultDialog = () => (
        <div className="text-center py-8">
            <p className="text-gray-500">No details available for this request type.</p>
        </div>
    );

    const TabButton = ({ tab, label, icon: Icon, activeTab, setActiveTab }) => (
        <button
            onClick={() => setActiveTab(tab)}
            className={`flex items-center px-3 py-2 text-sm font-medium rounded-md border border-gray-200 transition-colors whitespace-nowrap ${activeTab === tab
                ? 'bg-sidebar text-white dark:bg-sidebar-primary'
                : 'text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
        >
            {/* Icon only on mobile, icon + text on desktop */}
            {Icon && <Icon className="w-5 h-5 sm:mr-2" />}
            <span className="hidden sm:inline">{label}</span>
        </button>
    );

    // Add these icon components
    const LeaveIcon = ({ className = "w-4 h-4" }) => (
        <svg className={className} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        </svg>
    );

    const ReceiptIcon = ({ className = "w-4 h-4" }) => (
        <svg className={className} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clipRule="evenodd" />
        </svg>
    );

    const CurrencyIcon = ({ className = "w-4 h-4" }) => (
        <svg className={className} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 5a1 1 0 100 2h1a2 2 0 011.732 1H7a1 1 0 100 2h2.732A2 2 0 018 11H7a1 1 0 00-.707 1.707l3 3a1 1 0 001.414-1.414l-1.483-1.484A4.008 4.008 0 0011.874 10H13a1 1 0 100-2h-1.126a3.976 3.976 0 00-.41-1H13a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
    );

    return (
        <ContainerCard>

            <ModuleTitleCard
                Icon={LucideClipboardCheck}
                title={'Approval Inbox'}
                subtitle="Review and manage pending approval requests"
                totalRecords={totalRecords}
            />

            <Reactable
                pagename="approval inbox"
                tableTitle="Approval Inbox"
                columns={tableData.columns}
                data={tableData.data}
                setData={tableData.setData}
                url={tableData.url}
                setTotalCount={setTotalRecords}
                serverTrigger={serverTrigger}
                serverSide={true}
                rowSize="md"
                isCSVRequired
                isPDFRequired
                activeTab={activeTab}
                isDateRequired={false}
                isMonthYearRequired={false}
                isFromToDateRequired={false}
            >
                <div className="flex space-x-2 border-b border-gray-200 pb-2">
                    <div className="flex space-x-2 border-b border-gray-200 pb-2 overflow-x-auto">
                        <TabButton
                            tab="leave"
                            label="Leave Requests"
                            icon={LeaveIcon}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                        <TabButton
                            tab="reimbursement"
                            label="Reimbursements"
                            icon={ReceiptIcon}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                        <TabButton
                            tab="advance"
                            label="Advance Salary"
                            icon={CurrencyIcon}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                        {showOvertimeTab && (
                            <TabButton
                                tab="overtime"
                                label="Overtime"
                                icon={Clock}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                            />
                        )}
                        <TabButton
                            tab="resign"
                            label="Resignation"
                            icon={UserX}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                        <TabButton
                            tab="shiftchange"
                            label="Shift Change"
                            icon={ArrowLeftRight}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                    </div>
                </div>
                {activeTab === "overtime" && <PayrollProcessLockNotice className="mt-3" />}
            </Reactable>

            {/* Generic Approve Dialog for all request types */}
            {(() => {
                // Check if the request date has passed (for approve button)
                const isRequestDatePassed = () => {
                    if (!selectedRequest?.from_date) return false;

                    try {
                        const requestDate = new Date(selectedRequest.from_date);
                        const today = new Date();
                        requestDate.setHours(0, 0, 0, 0);
                        today.setHours(0, 0, 0, 0);
                        return requestDate < today;
                    } catch (error) {
                        return false;
                    }
                };

                // Only disable for leave requests when date has passed
                const isLeaveRequest = selectedRequest?.leave_request_type === 'leave' || selectedRequest?.approval_type === 'Leave';

                const shouldDisableApproveReject = selectedRequest?.status !== 'pending' && selectedRequest?.status !== 'cancelled' && selectedRequest?.status !== 'cancel inprogress';

                // Additional condition for leave requests: also disable if date has passed
                const shouldDisableLeaveApproveReject = isLeaveRequest && isRequestDatePassed();

                // Final disable condition: either status is not pending OR (for leave requests, date has passed)
                const finalDisableApproveReject = shouldDisableApproveReject || shouldDisableLeaveApproveReject;

                const canApprove = !shouldDisableApproveReject;

                return (
                    <DialogModal
                        open={showApproveDialog}
                        onClose={() => {
                            setShowApproveDialog(false);
                            setApprovalNotes('');
                            setDebitFrom('');
                            setApprovedEmiPeriod('');
                            setResignationApproval({ notice_period: '', last_working_day: null, approval_comments: '', is_payroll_hold: false });
                        }}
                        {...(canApprove ? { onSave: handleApproveRequest, saveLabel: 'Approve' } : { hideSaveButton: true })}
                        title={`Review ${tableData.type?.charAt(0).toUpperCase() + tableData.type?.slice(1)} Request`}
                        showCancel={false}
                        width="60rem"
                        className="max-w-[95vw]"
                        loading={approveLoading}
                    >
                        {renderDialogContent()}
                    </DialogModal>
                );
            })()}

            {/* Processed Confirmation Dialog — only fires from reimbursement flow.
                Backend validates that the reimbursement is in approved/partial state
                before flipping to processed and stamping processed_by/date. */}
            <DialogModal
                open={showProcessedDialog}
                onClose={() => {
                    setShowProcessedDialog(false);
                    setProcessedRemarks('');
                }}
                onSave={handleMarkProcessed}
                title="Mark Reimbursement as Processed"
                saveLabel="Confirm"
                width="500px"
                loading={processedLoading}
            >
                <div className="space-y-4">
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-md">
                        <p className="text-sm text-emerald-800 dark:text-emerald-300">
                            Confirm that payment / accounting processing has been completed for this reimbursement.
                            This will finalize the request and notify the employee.
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Remarks (optional)
                        </label>
                        <Textarea
                            value={processedRemarks}
                            onChange={(e) => setProcessedRemarks(e.target.value)}
                            placeholder="e.g. UTR / payment reference, batch number..."
                            rows={3}
                            showCharCount={true}
                            maxLength={250}
                            className="w-full"
                        />
                    </div>
                </div>
            </DialogModal>

            {/* Reject Confirmation Dialog — collects a required reason that is stored on the
                approval log (via approval_notes) so the requester knows why it was rejected. */}
            <DialogModal
                open={showRejectDialog}
                onClose={() => {
                    setShowRejectDialog(false);
                    setSelectedRequestForReject(null);
                    setRevertReason('');
                }}
                onSave={handleConfirmReject}
                title={`Reject ${tableData.type?.charAt(0).toUpperCase() + tableData.type?.slice(1)} request`}
                saveLabel="Reject"
                saveButtonColor="destructive"
                width="500px"
                loading={rejectLoading}
            >
                <div className="space-y-4">
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md">
                        <p className="text-sm text-red-800 dark:text-red-300">
                            Are you sure you want to reject this {tableData.type?.charAt(0).toUpperCase() + tableData.type?.slice(1)} request of{' '}
                            <strong className="font-semibold">
                                {selectedRequestForReject?.request_from?.employee_name
                                    ? selectedRequestForReject.request_from.employee_name.charAt(0).toUpperCase() + selectedRequestForReject.request_from.employee_name.slice(1)
                                    : 'Unknown Employee'}
                            </strong>{' '}
                            on{' '}
                            <strong className="font-semibold">
                                {selectedRequestForReject?.from_date || selectedRequestForReject?.date || selectedRequestForReject?.request_date || 'Unknown Date'}
                            </strong>
                            ? This action cannot be undone.
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Reason for Rejection <span className="text-red-500">*</span>
                        </label>
                        <Textarea
                            value={revertReason}
                            onChange={(e) => setRevertReason(e.target.value)}
                            placeholder="Please provide a reason for rejecting this request..."
                            rows={4}
                            showCharCount={true}
                            maxLength={250}
                            className="w-full"
                            required
                        />
                    </div>
                </div>
            </DialogModal>

            {/* Revert Approval Dialog (Generic for all types) */}
            <DialogModal
                open={showRevertDialog}
                onClose={() => {
                    setShowRevertDialog(false);
                    setRevertReason('');
                }}
                onSave={handleRevertApproval}
                title="Revert Approval"
                saveLabel="Revert"
                width="700px"
                loading={revertLoading}
            >
                {selectedRequest && (
                    <div className="space-y-4">
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-800">
                                You are about to revert the approval for this request. This action cannot be undone.
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Reason for Reverting Approval (Optional)
                            </label>
                            <Textarea
                                value={revertReason}
                                onChange={(e) => setRevertReason(e.target.value)}
                                placeholder="Please provide a reason for reverting this approval..."
                                rows={3}
                                showCharCount={true}
                                maxLength={250}
                                className={cn(
                                    "w-full rounded border transition-colors duration-200 text-xs",
                                    "border-input bg-background text-foreground",
                                    "focus:border-sidebar-primary focus:ring-1 focus:ring-sidebar-primary/20 dark:focus:border-sidebar-primary dark:focus:ring-sidebar-primary/30",
                                )}
                                required
                            />
                        </div>
                        <div className="text-sm flex items-center justify-between gap-2 text-gray-600 border-t pt-4">
                            <div>
                                {(() => {
                                    const record = selectedRequest;
                                    let empDetails = {
                                        ...record?.request_from,
                                        designation_id: record?.request_from?.designation
                                            ? {
                                                id: record?.request_from.designation.id,
                                                designation_name: record?.request_from.designation.name,
                                            }
                                            : { id: 0, designation_name: "" },
                                        first_name: record?.request_from?.employee_name || "-",
                                        id: record?.request_from?.employee_id || "-",
                                    };
                                    return <EmployeeDetails details={empDetails} size="xs" />;
                                })()}
                            </div>
                            <p><strong>Request Type:</strong> {tableData.type?.charAt(0).toUpperCase() + tableData.type?.slice(1)}</p>
                            <p><strong>Current Status:</strong> <StatusBadge status={selectedRequest.status} /></p>
                        </div>
                    </div>
                )}
            </DialogModal>

            {/* Approval Log Dialog (Generic for all types) */}
            <DialogModal
                open={showApprovalLog}
                onClose={() => setShowApprovalLog(false)}
                title="Approval Log"
                showActions={false}
                width="750px"
            >
                {selectedRequest && selectedRequest.approval_log && selectedRequest.approval_log.length > 0 ? (
                    <div className="space-y-4">
                        <div className="overflow-x-auto h-60 relative">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Date & Time
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Approver
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Notes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                    {selectedRequest.approval_log.map((log, index) => (
                                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {new Date(log.approved_date_and_time).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {log.approver_name || '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <StatusBadge status={log.approval_status} />
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                {log.approval_notes || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="text-sm flex items-center justify-between gap-2 text-gray-600 border-t pt-4">
                            <div>
                                {(() => {
                                    const record = selectedRequest;
                                    let empDetails = {
                                        ...record?.request_from,
                                        designation_id: record?.request_from?.designation
                                            ? {
                                                id: record?.request_from.designation.id,
                                                designation_name: record?.request_from.designation.name,
                                            }
                                            : { id: 0, designation_name: "" },
                                        first_name: record?.request_from?.employee_name || "-",
                                        id: record?.request_from?.employee_id || "-",
                                    };
                                    return <EmployeeDetails details={empDetails} size="xs" />;
                                })()}
                            </div>
                            <p><strong>Request Type:</strong> {tableData.type?.charAt(0).toUpperCase() + tableData.type?.slice(1)}</p>
                            <p><strong>Current Status:</strong> <StatusBadge status={selectedRequest.status} /></p>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-500">No approval log available for this request.</p>
                    </div>
                )}
            </DialogModal>

        </ContainerCard>
    );
}

export default ApprovalInbox;